import React, { useState, useRef, useEffect, useMemo } from 'react';
import { XMarkIcon } from 'components/icons';
import { NAICS_CODES } from '../../data/naicsCodes';

interface NaicsCodePickerProps {
  value: string[];
  onChange: (codes: string[]) => void;
  label?: string;
  helpText?: string;
  placeholder?: string;
}

/**
 * Official NAICS sectors, keyed by the first two digits of the code. Grouping
 * is derived from the code itself rather than from array order — the previous
 * version walked a hand-ordered list looking for start/end marker codes, which
 * only worked while the list was a curated 106 entries kept in a specific
 * sequence. It cannot survive the complete 1,012-code set.
 */
const NAICS_SECTORS: { prefixes: string[]; label: string }[] = [
  { prefixes: ['11'], label: 'Agriculture, Forestry, Fishing and Hunting' },
  { prefixes: ['21'], label: 'Mining, Quarrying, and Oil and Gas Extraction' },
  { prefixes: ['22'], label: 'Utilities' },
  { prefixes: ['23'], label: 'Construction' },
  { prefixes: ['31', '32', '33'], label: 'Manufacturing' },
  { prefixes: ['42'], label: 'Wholesale Trade' },
  { prefixes: ['44', '45'], label: 'Retail Trade' },
  { prefixes: ['48', '49'], label: 'Transportation and Warehousing' },
  { prefixes: ['51'], label: 'Information' },
  { prefixes: ['52'], label: 'Finance and Insurance' },
  { prefixes: ['53'], label: 'Real Estate and Rental and Leasing' },
  { prefixes: ['54'], label: 'Professional, Scientific, and Technical Services' },
  { prefixes: ['55'], label: 'Management of Companies and Enterprises' },
  { prefixes: ['56'], label: 'Administrative, Support, Waste Management and Remediation' },
  { prefixes: ['61'], label: 'Educational Services' },
  { prefixes: ['62'], label: 'Health Care and Social Assistance' },
  { prefixes: ['71'], label: 'Arts, Entertainment, and Recreation' },
  { prefixes: ['72'], label: 'Accommodation and Food Services' },
  { prefixes: ['81'], label: 'Other Services (except Public Administration)' },
  { prefixes: ['92'], label: 'Public Administration' },
];

function buildCategories() {
  return NAICS_SECTORS.map(sector => ({
    label: sector.label,
    codes: NAICS_CODES.filter(c => sector.prefixes.includes(c.code.slice(0, 2))),
  })).filter(cat => cat.codes.length > 0);
}

const NaicsCodePicker: React.FC<NaicsCodePickerProps> = ({
  value,
  onChange,
  label,
  helpText,
  placeholder = 'Type your industry, e.g. "IT services", "construction", "541512"...',
}) => {
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const categories = useMemo(() => buildCategories(), []);
  const [openSector, setOpenSector] = useState<string | null>(null);

  const filteredSuggestions = search
    ? NAICS_CODES.filter(n =>
        n.code.includes(search) ||
        n.description.toLowerCase().includes(search.toLowerCase())
      ).slice(0, 25)
    : [];

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const addCode = (code: string) => {
    if (!value.includes(code)) {
      onChange([...value, code]);
    }
    setSearch('');
  };

  const removeCode = (code: string) => {
    onChange(value.filter(c => c !== code));
  };

  return (
    <div ref={containerRef}>
      {label && <label className="block text-sm font-medium text-content mb-1">{label}</label>}
      {helpText && <p className="text-xs text-content-subtle mb-2">{helpText}</p>}

      {/* Selected codes as pills */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map(code => {
            const match = NAICS_CODES.find(n => n.code === code);
            return (
              <span key={code} className="inline-flex items-center gap-1 px-2 py-1 bg-brand-100 text-brand-700 text-xs rounded-full">
                {match ? match.description : code} ({code})
                <button type="button" onClick={() => removeCode(code)}>
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Search input */}
      <input
        type="text"
        placeholder={placeholder}
        value={search}
        onChange={e => { setSearch(e.target.value); setDropdownOpen(true); }}
        onFocus={() => setDropdownOpen(true)}
        className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:ring-2 focus:ring-brand-500"
      />

      {/* Dropdown: search results or browsable categories */}
      {dropdownOpen && (
        <div className="mt-1 border border-border rounded-lg max-h-48 overflow-y-auto bg-surface shadow-lg z-50 relative">
          {search && filteredSuggestions.length > 0 ? (
            filteredSuggestions.map(n => (
              <button
                key={n.code}
                type="button"
                onClick={() => addCode(n.code)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-brand-50 flex justify-between items-center border-b border-gray-50 last:border-0 ${value.includes(n.code) ? 'bg-brand-50 text-brand-700' : ''}`}
              >
                <span className="text-content">{n.description}</span>
                <span className="text-content-subtle text-xs ml-2 flex-shrink-0">{n.code}</span>
              </button>
            ))
          ) : search && filteredSuggestions.length === 0 ? (
            <div className="px-3 py-3 text-sm text-content-subtle text-center">No matches found — try a different term</div>
          ) : (
            categories.map(cat => (
              <div key={cat.label}>
                <button
                  type="button"
                  onClick={() => setOpenSector(openSector === cat.label ? null : cat.label)}
                  className="w-full text-left px-3 py-1.5 bg-surface-sunk text-xs font-semibold text-content-muted uppercase tracking-wide sticky top-0 flex justify-between items-center hover:text-content"
                >
                  <span>{cat.label}</span>
                  <span className="font-normal normal-case">{openSector === cat.label ? 'Hide' : `${cat.codes.length}`}</span>
                </button>
                {openSector === cat.label && cat.codes.map(n => (
                  <button
                    key={n.code}
                    type="button"
                    onClick={() => addCode(n.code)}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-brand-50 flex justify-between items-center ${value.includes(n.code) ? 'bg-brand-50 text-brand-700' : ''}`}
                  >
                    <span className="text-content">{n.description}</span>
                    <span className="text-content-subtle text-xs ml-2 flex-shrink-0">{n.code}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NaicsCodePicker;
